import { useState } from 'react';
import {
  useListAdvancesRetentionsQuery,
  useCreateAdvanceRetentionMutation,
  useListTaxCalendarQuery,
  useCreateTaxItemMutation,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { IconX } from '@shared/ui/icons';
import { useAgentPermission } from '@entities/user';
import {
  Container,
  AlertBanner,
} from './styled';
import { TaxBalanceSection } from './TaxBalanceSection';
import { AdvancesSection } from './AdvancesSection';
import { RetentionsSection } from './RetentionsSection';
import { CreateTaxModal } from './CreateTaxModal';
import { CreateAdvanceRetentionModal } from './CreateAdvanceRetentionModal';
import {
  currentQuarterKey,
  moscowTodayIso,
  quarterKpiState,
  recentQuarters,
} from './taxPeriods';

export function WorkingCapitalView() {
  const { can_write: canWrite } = useAgentPermission('finance');
  // Текущий квартал — по Москве, как на сервере, а не по часовому поясу компьютера
  const [vatQuarter, setVatQuarter] = useState(() => currentQuarterKey());
  const {
    data: advances,
    isLoading: isAdvLoading,
    isError: isAdvError,
    refetch: refetchAdv,
  } = useListAdvancesRetentionsQuery();
  // Строки календаря от квартала не зависят — их берём из последнего ответа (`data`);
  // оценка НДС — только из ответа по выбранному кварталу (`currentData`)
  const {
    data: taxData,
    currentData: quarterTaxData,
    isFetching: isTaxFetching,
    isError: isTaxError,
    refetch: refetchTax,
  } = useListTaxCalendarQuery({ vat_quarter: vatQuarter });
  const taxKpi = quarterKpiState({ currentData: quarterTaxData, isError: isTaxError });

  const [createTaxItem, { isLoading: isCreatingTax }] = useCreateTaxItemMutation();
  const [createAdvRetention, { isLoading: isCreatingAdv }] = useCreateAdvanceRetentionMutation();

  const [taxModalOpen, setTaxModalOpen] = useState(false);
  const [advModalOpen, setAdvModalOpen] = useState(false);
  const [banner, setBanner] = useState<{ text: string; tone: 'success' | 'warning' } | null>(null);

  // Разделение авансов и гарантийных удержаний
  const advanceRows = (advances ?? []).filter(
    (a) => a.direction === 'payable_advance' || a.direction === 'receivable_advance',
  );
  const retentionRows = (advances ?? []).filter(
    (a) => a.direction === 'receivable_retention' || a.direction === 'payable_retention',
  );

  // Ошибку сохранения не ловим здесь: окно покажет её у формы и оставит введённое
  async function handleCreateTax(data: {
    taxPeriod: string;
    taxDueDate: string;
    taxType: string;
    taxTitle: string;
    taxAmount: number;
    taxReserved?: number;
  }) {
    await createTaxItem({
      tax_period: data.taxPeriod,
      due_date: data.taxDueDate,
      tax_type: data.taxType,
      title: data.taxTitle,
      accrued_amount: data.taxAmount,
      reserved_amount: data.taxReserved,
    }).unwrap();
    setTaxModalOpen(false);
    setBanner({
      text: 'Налоговое обязательство добавлено в календарь ЕНС.',
      tone: 'success',
    });
  }

  async function handleCreateAdv(data: {
    direction: string;
    counterpartyName: string;
    contractNumber: string;
    projectName: string;
    initialAmount: number;
    expectedCloseDate?: string;
  }) {
    await createAdvRetention({
      direction: data.direction,
      counterparty_name: data.counterpartyName,
      contract_number: data.contractNumber,
      project_name: data.projectName,
      initial_amount: data.initialAmount,
      expected_close_date: data.expectedCloseDate,
    }).unwrap();
    setAdvModalOpen(false);
    setBanner({
      text: 'Запись об авансе или гарантийном удержании добавлена.',
      tone: 'success',
    });
  }

  return (
    <Container>
      {banner && (
        <AlertBanner $tone={banner.tone}>
          <span>{banner.text}</span>
          <Button $variant="ghost" $size="sm" onClick={() => setBanner(null)} aria-label="Закрыть">
            <IconX size={14} />
          </Button>
        </AlertBanner>
      )}

      {/* Секция 1: Оценка НДС за квартал и налоговый резерв */}
      <TaxBalanceSection
        kpi={taxKpi}
        items={taxData?.items}
        isItemsLoading={!taxData && isTaxFetching}
        isItemsError={!taxData && isTaxError}
        refetchTax={refetchTax}
        onOpenTaxModal={() => setTaxModalOpen(true)}
        canWrite={Boolean(canWrite)}
        vatQuarter={vatQuarter}
        quarterOptions={recentQuarters(moscowTodayIso(), 5)}
        onChangeQuarter={setVatQuarter}
      />

      {/* Секция 2: Неотработанные авансы */}
      <AdvancesSection
        advanceRows={advanceRows}
        isLoading={isAdvLoading}
        isError={isAdvError}
        refetch={refetchAdv}
        onOpenAdvModal={() => setAdvModalOpen(true)}
        canWrite={Boolean(canWrite)}
      />

      {/* Секция 3: Гарантийные удержания */}
      <RetentionsSection
        retentionRows={retentionRows}
        isLoading={isAdvLoading}
        isError={isAdvError}
        refetch={refetchAdv}
      />

      {/* Модалка: Добавление налога ЕНС */}
      <CreateTaxModal
        open={taxModalOpen}
        onClose={() => setTaxModalOpen(false)}
        onCreateTax={handleCreateTax}
        isCreating={isCreatingTax}
      />

      {/* Модалка: Добавление аванса / удержания */}
      <CreateAdvanceRetentionModal
        open={advModalOpen}
        onClose={() => setAdvModalOpen(false)}
        onCreateAdv={handleCreateAdv}
        isCreating={isCreatingAdv}
      />
    </Container>
  );
}
